import * as ort from 'onnxruntime-web';
import LIGHT_CLASSIF_MODEL from '../MLmodels/light-classif.onnx';

export const LIGHT_MODEL_INTERVAL_MS = 500;
export const LIGHT_MODEL_OK_VALIDITY_MS = 2500;

const LIGHT_MODEL_INPUT_SIZE = 512;
const LIGHT_MODEL_OK_INDEX = 1;
const LIGHT_MODEL_OK_THRESHOLD = 0.6;
const LIGHT_MODEL_LABELS = ['too_dark', 'ok', 'uneven'];
const LIGHT_MODEL_WASM_CDN = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.26.0/dist/';

export type LightModelResult = {
	okScore: number;
	topPrediction: {
		index: number;
		value: number;
		label: string;
	};
};

export const loadLightModel = async () => {
	ort.env.wasm.numThreads = 1;
	ort.env.wasm.wasmPaths = LIGHT_MODEL_WASM_CDN;

	return ort.InferenceSession.create(LIGHT_CLASSIF_MODEL, {
		executionProviders: ['wasm'],
		graphOptimizationLevel: 'all',
	});
};

const captureLightModelFrame = (video: HTMLVideoElement, previewCanvas?: HTMLCanvasElement | null) => {
	const sourceWidth = video.videoWidth;
	const sourceHeight = video.videoHeight;

	if (!sourceWidth || !sourceHeight) {
		return null;
	}

	// Same centered square crop as Sisley's CameraView, before resizing to 512 × 512.
	const cropSize = Math.min(sourceWidth, sourceHeight);
	const sx = Math.floor((sourceWidth - cropSize) / 2);
	const sy = Math.floor((sourceHeight - cropSize) / 2);
	const canvas = previewCanvas ?? document.createElement('canvas');
	canvas.width = LIGHT_MODEL_INPUT_SIZE;
	canvas.height = LIGHT_MODEL_INPUT_SIZE;
	const context = canvas.getContext('2d');

	if (!context) {
		return null;
	}

	context.drawImage(video, sx, sy, cropSize, cropSize, 0, 0, LIGHT_MODEL_INPUT_SIZE, LIGHT_MODEL_INPUT_SIZE);

	const imageData = context.getImageData(0, 0, LIGHT_MODEL_INPUT_SIZE, LIGHT_MODEL_INPUT_SIZE).data;
	const pixelCount = LIGHT_MODEL_INPUT_SIZE * LIGHT_MODEL_INPUT_SIZE;
	const input = new Float32Array(3 * pixelCount);

	for (let index = 0; index < pixelCount; index += 1) {
		input[index] = imageData[index * 4] / 255;
		input[pixelCount + index] = imageData[index * 4 + 1] / 255;
		input[pixelCount * 2 + index] = imageData[index * 4 + 2] / 255;
	}

	return new ort.Tensor('float32', input, [1, 3, LIGHT_MODEL_INPUT_SIZE, LIGHT_MODEL_INPUT_SIZE]);
};

const getTopPrediction = (values: number[]) => {
	let index = 0;
	let value = values[0] ?? 0;

	for (let currentIndex = 1; currentIndex < values.length; currentIndex += 1) {
		if (values[currentIndex] > value) {
			index = currentIndex;
			value = values[currentIndex];
		}
	}

	return { index, value };
};

export const runLightModel = async (
	session: ort.InferenceSession,
	video: HTMLVideoElement,
	previewCanvas?: HTMLCanvasElement | null,
): Promise<LightModelResult | null> => {
	const tensor = captureLightModelFrame(video, previewCanvas);
	if (!tensor) {
		return null;
	}

	const inputName = session.inputNames[0];
	const outputName = session.outputNames[0];
	const outputs = await session.run({ [inputName]: tensor });
	const values = Array.from(outputs[outputName].data as Float32Array);
	const topPrediction = getTopPrediction(values);

	return {
		okScore: values[LIGHT_MODEL_OK_INDEX] ?? 0,
		topPrediction: {
			...topPrediction,
			label: LIGHT_MODEL_LABELS[topPrediction.index] ?? `class_${topPrediction.index}`,
		},
	};
};

export const isLightModelResultOk = (result: LightModelResult | null) => {
	return (result?.okScore ?? 0) > LIGHT_MODEL_OK_THRESHOLD;
};
